import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AlertComponent, DisplayInputComponent } from '@components';
import { Status } from '@enums';
import {
  CriminalRecordCheckDocument,
  Signature,
  User,
  UserCriminalRecordCheck,
} from '@models';
import { UtilService } from '@services';
import * as CriminalRecordCheckActions from '@state/criminal-record-check/criminal-record-check.actions';
import * as UserActions from '@state/user/user.actions';

@Component({
  selector: 'app-user-criminal-record-check',
  standalone: true,
  imports: [
    CommonModule,
    DisplayInputComponent,
    MatTabGroup,
    MatTab,
    MatIcon,
    MatProgressSpinner,
    MatButtonModule,
    AlertComponent,
  ],
  templateUrl: './criminal-record-check.page.html',
  styleUrl: './criminal-record-check.page.scss',
})
export class UserCriminalRecordCheckComponent {
  @Input() user: User | null = null;

  @Input() criminalRecordCheckDocument: CriminalRecordCheckDocument | null =
    null;

  public safeDocumentUrl: SafeResourceUrl = '';
  public dragActive = false;
  public selectedFile?: File;
  public uploadPreview: SafeResourceUrl | null = null;
  public loading = false;
  public error: string | null = null;

  constructor(private sanitizer: DomSanitizer, public utils: UtilService) {}

  ngOnInit(): void {
    if (
      this.signature?.fileUrl ??
      this.signedCriminalRecordCheckDocument?.fileUrl
    )
      this.safeDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.signature?.fileUrl ??
          this.signedCriminalRecordCheckDocument?.fileUrl ??
          ''
      );

    if (this.userCriminalRecordCheck?.fileUrl)
      this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.userCriminalRecordCheck?.fileUrl ?? ''
      );
  }

  public get userCriminalRecordCheck(): UserCriminalRecordCheck | undefined {
    return this.user?.criminalRecordCheck;
  }

  public get signedCriminalRecordCheckDocument():
    | CriminalRecordCheckDocument
    | undefined {
    return this.userCriminalRecordCheck?.criminalRecordCheckDocument;
  }

  public get signature(): Signature | undefined {
    return this.userCriminalRecordCheck?.signature;
  }

  public get signedOn(): string {
    return JSON.stringify(this.signature?.createdAt).substring(1, 11);
  }

  public get signatureOutOfDate(): boolean {
    return (
      this.userCriminalRecordCheck?.criminalRecordCheckDocumentId !==
      this.criminalRecordCheckDocument?.id
    );
  }

  public onFileSelected(event: Event): void {
    try {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        this.selectedFile = target.files[0];
        this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(this.selectedFile)
        );
        this.error = null;
      }
    } catch (e) {
      this.error = 'Failed to select file. Please try again.';
    }
  }

  public closePreview(): void {
    this.uploadPreview = null;
    this.selectedFile = undefined;
  }

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragActive = true;
  }

  public onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragActive = false;
  }

  public onFileDrop(event: DragEvent) {
    try {
      event.preventDefault();
      this.dragActive = false;
      if (event.dataTransfer && event.dataTransfer.files.length > 0) {
        this.selectedFile = event.dataTransfer.files[0];
        this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(this.selectedFile)
        );
        this.error = null;
      }
    } catch (e) {
      this.error = 'Failed to drop file. Please try again.';
    }
  }

  async uploadUserCriminalRecordCheck(): Promise<void> {
    if (this.selectedFile) {
      this.loading = true;
      try {
        await this.utils.actionsToPromise(
          CriminalRecordCheckActions.uploadProcessedUserCriminalRecordCheckDocument(
            {
              userCriminalRecordCheck: this.userCriminalRecordCheck!,
              file: this.selectedFile,
            }
          ),
          CriminalRecordCheckActions.uploadProcessedUserCriminalRecordCheckDocumentSuccess,
          CriminalRecordCheckActions.uploadProcessedUserCriminalRecordCheckDocumentFailed
        );
      } catch (err: any) {
        this.error = this.utils.getErrorMessage(err);
      } finally {
        this.loading = false;
        if (!this.error) {
          this.selectedFile = undefined;
        }
      }
    }
  }

  onDeclineClicked(): void {
    this.saveStatus(Status.DECLINED);
  }

  onApproveClicked(): void {
    this.saveStatus(Status.APPROVED);
  }

  private async saveStatus(status: Status) {
    this.error = '';
    this.loading = true;

    try {
      const userCriminalRecordCheck = this.userCriminalRecordCheck!;
      await this.utils.actionsToPromise(
        UserActions.setUserCriminalRecordCheckStatus({
          userCriminalRecordCheck,
          status,
        }),
        UserActions.setUserCriminalRecordCheckStatusSuccess,
        UserActions.setUserCriminalRecordCheckStatusFailed
      );
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    }

    this.loading = false;
  }
}
